import { LocalesKey } from '../../../../../locales/keys/localesKeysTypes';
import { modelViewersKeys } from '../../../../../locales/keys';

export const enum ModelViewerAspectRatioValue {
  RATIO_1_1 = '1:1',
  RATIO_1_4 = '1:4',
  RATIO_4_1 = '4:1',
  RATIO_1_8 = '1:8',
  RATIO_8_1 = '8:1',
  RATIO_2_3 = '2:3',
  RATIO_3_2 = '3:2',
  RATIO_3_4 = '3:4',
  RATIO_4_3 = '4:3',
  RATIO_4_5 = '4:5',
  RATIO_5_4 = '5:4',
  RATIO_9_16 = '9:16',
  RATIO_16_9 = '16:9',
  RATIO_21_9 = '21:9'
}

export const enum ModelViewerImageSizeValue {
  SIZE_512 = '512',
  SIZE_1K = '1K',
  SIZE_2K = '2K',
  SIZE_4K = '4K'
}

/**
 * "Auto" is a UI-only selection: the frame is left unconstrained and the
 * effective ratio is resolved from the live viewer box at request time (see
 * resolveModelViewerAspectRatio). It is deliberately NOT a member of
 * ModelViewerAspectRatioValue, so no request param or CSS value can ever
 * receive it.
 */
export const MODEL_VIEWER_ASPECT_RATIO_AUTO = 'auto' as const;

export type ModelViewerAspectRatioSelection =
  | typeof MODEL_VIEWER_ASPECT_RATIO_AUTO
  | ModelViewerAspectRatioValue;

// Fallback for an unmeasurable viewer box (zero-sized before layout).
export const DEFAULT_MODEL_VIEWER_ASPECT_RATIO =
  ModelViewerAspectRatioValue.RATIO_16_9;

export const DEFAULT_MODEL_VIEWER_ASPECT_RATIO_SELECTION =
  MODEL_VIEWER_ASPECT_RATIO_AUTO;

export const DEFAULT_MODEL_VIEWER_IMAGE_SIZE =
  ModelViewerImageSizeValue.SIZE_1K;

export interface ModelViewerAspectRatioGroup {
  i18nLabel: LocalesKey;
  values: ModelViewerAspectRatioValue[];
}

// Menu grouping: Square / Landscape / Portrait, in design order.
export const modelViewerAspectRatioGroups: ModelViewerAspectRatioGroup[] = [
  {
    i18nLabel: modelViewersKeys.aspectRatioSquare,
    values: [ModelViewerAspectRatioValue.RATIO_1_1]
  },
  {
    i18nLabel: modelViewersKeys.aspectRatioLandscape,
    values: [
      ModelViewerAspectRatioValue.RATIO_5_4,
      ModelViewerAspectRatioValue.RATIO_4_3,
      ModelViewerAspectRatioValue.RATIO_3_2,
      ModelViewerAspectRatioValue.RATIO_16_9,
      ModelViewerAspectRatioValue.RATIO_21_9,
      ModelViewerAspectRatioValue.RATIO_4_1,
      ModelViewerAspectRatioValue.RATIO_8_1
    ]
  },
  {
    i18nLabel: modelViewersKeys.aspectRatioPortrait,
    values: [
      ModelViewerAspectRatioValue.RATIO_4_5,
      ModelViewerAspectRatioValue.RATIO_3_4,
      ModelViewerAspectRatioValue.RATIO_2_3,
      ModelViewerAspectRatioValue.RATIO_9_16,
      ModelViewerAspectRatioValue.RATIO_1_4,
      ModelViewerAspectRatioValue.RATIO_1_8
    ]
  }
];

export const modelViewerAspectRatioValues: Record<
  ModelViewerAspectRatioValue,
  { width: number; height: number }
> = {
  [ModelViewerAspectRatioValue.RATIO_1_1]: { width: 1, height: 1 },
  [ModelViewerAspectRatioValue.RATIO_1_4]: { width: 1, height: 4 },
  [ModelViewerAspectRatioValue.RATIO_4_1]: { width: 4, height: 1 },
  [ModelViewerAspectRatioValue.RATIO_1_8]: { width: 1, height: 8 },
  [ModelViewerAspectRatioValue.RATIO_8_1]: { width: 8, height: 1 },
  [ModelViewerAspectRatioValue.RATIO_2_3]: { width: 2, height: 3 },
  [ModelViewerAspectRatioValue.RATIO_3_2]: { width: 3, height: 2 },
  [ModelViewerAspectRatioValue.RATIO_3_4]: { width: 3, height: 4 },
  [ModelViewerAspectRatioValue.RATIO_4_3]: { width: 4, height: 3 },
  [ModelViewerAspectRatioValue.RATIO_4_5]: { width: 4, height: 5 },
  [ModelViewerAspectRatioValue.RATIO_5_4]: { width: 5, height: 4 },
  [ModelViewerAspectRatioValue.RATIO_9_16]: { width: 9, height: 16 },
  [ModelViewerAspectRatioValue.RATIO_16_9]: { width: 16, height: 9 },
  [ModelViewerAspectRatioValue.RATIO_21_9]: { width: 21, height: 9 }
};

export const modelViewerImageSizes: ModelViewerImageSizeValue[] = [
  ModelViewerImageSizeValue.SIZE_512,
  ModelViewerImageSizeValue.SIZE_1K,
  ModelViewerImageSizeValue.SIZE_2K,
  ModelViewerImageSizeValue.SIZE_4K
];
